/**
 * @fileoverview RecentDealCard component
 * Dumb component that renders a single deal card with header and content sections
 * Composes CardHeader and CardContent sub-components
 * Handles click events for card and title interactions with memoized callbacks
 *
 * This is a presentational component that receives all data as props and doesn't
 * manage any internal state. It delegates data fetching to parent components.
 */

import { useCallback } from 'react';
import CardHeader from '../CardHeader/CardHeader';
import CardContent from '../CardContent/CardContent';
import { StyledCard } from './RecentDealCard.style';
import type { RecentDealCardProps } from '../../types';

/**
 * RecentDealCard component
 * Renders a card displaying a single recent deal with:
 * - Header section with customer name (clickable) and dealer name
 * - Content section with vehicle details and deal metadata
 * - Active/selection state styling
 *
 * Supports two interaction patterns:
 * - Card click: Opens deal details in same tab/current view
 * - Title click: Can open deal in new tab (propagation stopped to prevent card click)
 *
 * @param deal - The recent deal object containing all display data
 * @param onCardClick - Callback when card is clicked (typically opens deal in current view)
 * @param onTitleClick - Callback when customer name/title is clicked (can open in new tab)
 * @returns A styled card component with header and content sections
 */
const RecentDealCard: React.FC<RecentDealCardProps> = ({ deal, onCardClick, onTitleClick }) => {
    const handleCardClick = useCallback(() => {
        onCardClick(deal);
    }, [deal, onCardClick]);

    const handleTitleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onTitleClick(deal);
        },
        [deal, onTitleClick]
    );

    return (
        <div className="deal-card" data-testid="recent-deal-card" onClick={handleCardClick}>
            <StyledCard
                className={deal.className}
                header={<CardHeader customerName={deal.displayName} dealerName={deal.dealerName} onTitleClick={handleTitleClick} />}
                content={<CardContent deal={deal} />}
            />
        </div>
    );
};

export default RecentDealCard;
